import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Pagination from "components/pagination";
import { apiCall } from "utils/apiClient";
import { useNavigate } from "react-router-dom";

const SERVICE_OFFERS_CREATE_ENDPOINT = "admin/service-offers/create";

const toCurrentTimeInputValue = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

const normalizeTimeToSeconds = (timeValue) => {
  const raw = String(timeValue || "").trim();
  if (!raw) return "00:00:00";
  const [hour = "00", minute = "00", second = "00"] = raw.split(":");
  return `${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:${second.padStart(2, "0")}`;
};

const composeDateTime = (dateValue, timeValue) => {
  const date = String(dateValue || "").trim();
  if (!date) return "";
  return `${date} ${normalizeTimeToSeconds(timeValue)}`;
};

const AddServiceOffer = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    discount_type: "percentage",
    discount_value: "",
    start_date: "",
    start_time: toCurrentTimeInputValue(),
    end_date: "",
    end_time: "23:59",
    priority: "",
    is_active: true,
  });
  const [targetType, setTargetType] = useState("service_type");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [services, setServices] = useState([]);

  const [selectedServiceTypeIds, setSelectedServiceTypeIds] = useState([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);

  const [serviceTypeSearch, setServiceTypeSearch] = useState("");
  const [serviceSearch, setServiceSearch] = useState("");

  const [serviceTypePage, setServiceTypePage] = useState(1);
  const [serviceTypePerPage, setServiceTypePerPage] = useState(10);
  const [servicePage, setServicePage] = useState(1);
  const [servicePerPage, setServicePerPage] = useState(10);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [serviceTypesRes, servicesRes] = await Promise.all([
          apiCall({ endpoint: "services-type", method: "GET" }),
          apiCall({ endpoint: "services", method: "GET" }),
        ]);

        setServiceTypes(Array.isArray(serviceTypesRes?.data) ? serviceTypesRes.data : []);
        setServices(Array.isArray(servicesRes?.data) ? servicesRes.data : []);
      } catch (fetchError) {
        setServiceTypes([]);
        setServices([]);
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    setError("");
    setServiceTypeSearch("");
    setServiceSearch("");
    setServiceTypePage(1);
    setServicePage(1);
  }, [targetType]);

  useEffect(() => {
    setServiceTypePage(1);
  }, [serviceTypeSearch, serviceTypePerPage]);

  useEffect(() => {
    setServicePage(1);
  }, [serviceSearch, servicePerPage]);

  const filteredServiceTypes = useMemo(() => {
    const keyword = serviceTypeSearch.trim().toLowerCase();
    if (!keyword) return serviceTypes;
    return serviceTypes.filter((item) =>
      String(item?.name || "").toLowerCase().includes(keyword)
    );
  }, [serviceTypes, serviceTypeSearch]);

  const filteredServices = useMemo(() => {
    const keyword = serviceSearch.trim().toLowerCase();
    if (!keyword) return services;
    return services.filter((item) => String(item?.name || "").toLowerCase().includes(keyword));
  }, [services, serviceSearch]);

  const serviceTypeTotalPages = Math.max(
    1,
    Math.ceil(filteredServiceTypes.length / serviceTypePerPage)
  );
  const serviceTypeStart = (serviceTypePage - 1) * serviceTypePerPage;
  const serviceTypePageData = filteredServiceTypes.slice(
    serviceTypeStart,
    serviceTypeStart + serviceTypePerPage
  );
  const serviceTypeDisplayedCount = Math.min(
    serviceTypePage * serviceTypePerPage,
    filteredServiceTypes.length
  );

  const serviceTotalPages = Math.max(1, Math.ceil(filteredServices.length / servicePerPage));
  const serviceStart = (servicePage - 1) * servicePerPage;
  const servicePageData = filteredServices.slice(serviceStart, serviceStart + servicePerPage);
  const serviceDisplayedCount = Math.min(servicePage * servicePerPage, filteredServices.length);

  useEffect(() => {
    if (serviceTypePage > serviceTypeTotalPages) setServiceTypePage(serviceTypeTotalPages);
  }, [serviceTypePage, serviceTypeTotalPages]);

  useEffect(() => {
    if (servicePage > serviceTotalPages) setServicePage(serviceTotalPages);
  }, [servicePage, serviceTotalPages]);

  const allFilteredServiceTypesSelected =
    filteredServiceTypes.length > 0 &&
    filteredServiceTypes.every((item) => selectedServiceTypeIds.includes(Number(item.id)));

  const allFilteredServicesSelected =
    filteredServices.length > 0 &&
    filteredServices.every((item) => selectedServiceIds.includes(Number(item.id)));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleServiceType = (id) => {
    const targetId = Number(id);
    setSelectedServiceTypeIds((prev) =>
      prev.includes(targetId) ? prev.filter((item) => item !== targetId) : [...prev, targetId]
    );
  };

  const toggleService = (id) => {
    const targetId = Number(id);
    setSelectedServiceIds((prev) =>
      prev.includes(targetId) ? prev.filter((item) => item !== targetId) : [...prev, targetId]
    );
  };

  const toggleSelectAllFilteredServiceTypes = () => {
    const filteredIds = filteredServiceTypes.map((item) => Number(item.id));
    if (allFilteredServiceTypesSelected) {
      setSelectedServiceTypeIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
      return;
    }
    setSelectedServiceTypeIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const toggleSelectAllFilteredServices = () => {
    const filteredIds = filteredServices.map((item) => Number(item.id));
    if (allFilteredServicesSelected) {
      setSelectedServiceIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
      return;
    }
    setSelectedServiceIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (targetType === "service_type" && selectedServiceTypeIds.length === 0) {
      setError("Please select at least one service type.");
      setSubmitting(false);
      return;
    }

    if (targetType === "service" && selectedServiceIds.length === 0) {
      setError("Please select at least one service.");
      setSubmitting(false);
      return;
    }

    let targets = [];
    if (targetType === "service_type") {
      targets = selectedServiceTypeIds.map((id) => ({
        target_type: "category",
        category_id: Number(id),
      }));
    } else if (targetType === "service") {
      targets = selectedServiceIds.map((id) => ({
        target_type: "service",
        service_id: Number(id),
      }));
    }

    const { start_time: startTime, end_time: endTime, ...restForm } =
      form;
    const payload = {
      ...restForm,
      start_date: composeDateTime(form.start_date, startTime),
      end_date: composeDateTime(form.end_date, endTime),
      discount_value: Number(form.discount_value),
      priority: Number(form.priority),
      is_active: form.is_active ? 1 : 0,
      targets,
    };

    try {
      const result = await apiCall({
        endpoint: SERVICE_OFFERS_CREATE_ENDPOINT,
        method: "POST",
        data: payload,
      });
      if (result?.status === 200 || result?.status === 201) {
        navigate("/service-offers");
      } else {
        setError(result?.message || "Failed to create service offer");
      }
    } catch (submitError) {
      setError("Failed to create service offer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="px-4 py-4 w-full">
        <h2 className="text-xl font-semibold mb-3 text-gray-800">Add Service Offer</h2>
        <form onSubmit={handleSubmit} className="bg-white p-4 rounded-md shadow-sm space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Title</label>
              <input
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder="Title"
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Discount Type</label>
              <select
                name="discount_type"
                value={form.discount_type}
                onChange={handleChange}
                className="w-full border px-2 py-1.5 rounded text-sm"
              >
                <option value="percentage">Percentage</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Discount Value</label>
              <input
                name="discount_value"
                value={form.discount_value}
                onChange={handleChange}
                required
                placeholder="Discount Value"
                type="number"
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Priority</label>
              <input
                name="priority"
                value={form.priority}
                onChange={handleChange}
                required
                placeholder="Priority"
                type="number"
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Start Date</label>
              <input
                type="date"
                name="start_date"
                value={form.start_date}
                onChange={handleChange}
                required
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">Start Time</label>
              <input
                type="time"
                name="start_time"
                value={form.start_time}
                onChange={handleChange}
                required
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">End Date</label>
              <input
                type="date"
                name="end_date"
                value={form.end_date}
                onChange={handleChange}
                required
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium text-xs text-gray-700">End Time</label>
              <input
                type="time"
                name="end_time"
                value={form.end_time}
                onChange={handleChange}
                required
                className="w-full border px-2 py-1.5 rounded text-sm"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block mb-1 font-medium text-xs text-gray-700">Target</label>
              <select
                value={targetType}
                onChange={(e) => setTargetType(e.target.value)}
                className="w-full border px-2 py-1.5 rounded text-sm"
              >
                <option value="service_type">Service Type</option>
                <option value="service">Service</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-gray-700">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
                Active
              </label>
            </div>
          </div>

          {targetType === "service_type" && (
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-sm font-semibold text-gray-700">
                  Service Types ({selectedServiceTypeIds.length} selected)
                </div>
                <input
                  type="text"
                  value={serviceTypeSearch}
                  onChange={(e) => setServiceTypeSearch(e.target.value)}
                  placeholder="Search service type..."
                  className="border px-2 py-1 rounded text-xs w-full max-w-xs"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-2 w-16">
                        <input
                          type="checkbox"
                          checked={allFilteredServiceTypesSelected}
                          onChange={toggleSelectAllFilteredServiceTypes}
                        />
                      </th>
                      <th className="text-left p-2 w-20">Sr No</th>
                      <th className="text-left p-2">Service Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceTypePageData.length > 0 ? (
                      serviceTypePageData.map((item, index) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedServiceTypeIds.includes(Number(item.id))}
                              onChange={() => toggleServiceType(item.id)}
                            />
                          </td>
                          <td className="p-2">{serviceTypeStart + index + 1}</td>
                          <td className="p-2">{item.name || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="p-2 text-center text-gray-500">
                          No service type found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={serviceTypePage}
                totalPages={serviceTypeTotalPages}
                onPageChange={setServiceTypePage}
                recordsPerPage={serviceTypePerPage}
                onRecordsPerPageChange={setServiceTypePerPage}
                totalRecords={filteredServiceTypes.length}
                count={serviceTypeDisplayedCount}
              showFirstLastControls
              compact
              />
            </div>
          )}

          {targetType === "service" && (
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-sm font-semibold text-gray-700">
                  Services ({selectedServiceIds.length} selected)
                </div>
                <input
                  type="text"
                  value={serviceSearch}
                  onChange={(e) => setServiceSearch(e.target.value)}
                  placeholder="Search service..."
                  className="border px-2 py-1 rounded text-xs w-full max-w-xs"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-2 w-16">
                        <input
                          type="checkbox"
                          checked={allFilteredServicesSelected}
                          onChange={toggleSelectAllFilteredServices}
                        />
                      </th>
                      <th className="text-left p-2 w-20">Sr No</th>
                      <th className="text-left p-2">Service</th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicePageData.length > 0 ? (
                      servicePageData.map((item, index) => (
                        <tr key={item.id} className="border-b">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedServiceIds.includes(Number(item.id))}
                              onChange={() => toggleService(item.id)}
                            />
                          </td>
                          <td className="p-2">{serviceStart + index + 1}</td>
                          <td className="p-2">{item.name || "-"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="p-2 text-center text-gray-500">
                          No service found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <Pagination
                currentPage={servicePage}
                totalPages={serviceTotalPages}
                onPageChange={setServicePage}
                recordsPerPage={servicePerPage}
                onRecordsPerPageChange={setServicePerPage}
                totalRecords={filteredServices.length}
                count={serviceDisplayedCount}
              showFirstLastControls
              compact
              />
            </div>
          )}

          {error && <div className="text-red-500 text-xs">{error}</div>}
          <div className="flex gap-2 justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-1.5 bg-indigo-600 text-white rounded text-sm"
            >
              {submitting ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/service-offers")}
              className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded text-sm"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AddServiceOffer;
